// src/api/dataWarehouse.ts

import { apiJson } from "./client";

export type DwhStatus = {
  // Which engine serves this account: "bigquery" | "snowflake" | null
  // (ADR-0175). The settings page branches on it so an ACTIVE Snowflake
  // account is not rendered as an empty BigQuery panel.
  warehouse_kind: string | null;
  project_id: string | null;
  storage_mode: string | null;
  bq_location: string | null;
  provisioning_status: string | null;
  error_message: string | null;
  runtime_sa_email: string | null;
  suggested_project_name: string | null;
  iam_grant_started_at: string | null;
  // Environment-derived naming contract from the backend (source of truth) so
  // the UI never hardcodes the suffix — staging gets "-stg-vrt-dwh", prod
  // "-vrt-dwh", local "-dev-dwh", with the matching name-length budget.
  project_suffix: string;
  max_project_name_length: number;
  // Change-location gating (ADR-0058): the region can only be changed while no
  // source has materialized data in the DWH. Server-authoritative — the UI must
  // not re-derive this.
  materializing_source_count: number;
  can_change_location: boolean;
};

/** GET /accounts/dwh */
export async function getDwhStatus(): Promise<DwhStatus> {
  return apiJson<DwhStatus>("/accounts/dwh", { unwrapData: false });
}

/**
 * GET /authorizations/bigquery/runtime-sa-email — get-or-create (the backend
 * mints the per-account runtime SA on demand, idempotent). Used where a flow
 * needs a grantable SA for an account whose DWH itself is not BigQuery: a
 * Snowflake tenant connecting a BigQuery-resident source (GA4 BQ Export)
 * still grants this SA read access on their export dataset — the
 * landingsbrug reads through it (fase 5b).
 */
export async function getOrCreateRuntimeSaEmail(): Promise<{ sa_email: string }> {
  return apiJson<{ sa_email: string }>("/authorizations/bigquery/runtime-sa-email", {
    unwrapData: false,
  });
}

/** GET /accounts/dwh/regions — curated BigQuery regions, backend source of truth */
export type DwhRegionOption = { value: string; label: string };
export type DwhRegionGroup = {
  multi_region: string;
  label: string;
  regions: DwhRegionOption[];
};
export type DwhRegions = { groups: DwhRegionGroup[] };

export async function getDwhRegions(): Promise<DwhRegions> {
  return apiJson<DwhRegions>("/accounts/dwh/regions", { unwrapData: false });
}

/** Mantine Select grouped data (multi-region heading → multi-region + its regions). */
export function regionsToSelectData(regions: DwhRegions | null) {
  if (!regions) return [];
  return regions.groups.map((g) => ({
    group: g.multi_region,
    items: [
      { value: g.multi_region, label: g.label },
      ...g.regions.map((r) => ({ value: r.value, label: r.label })),
    ],
  }));
}

/** PATCH /accounts/dwh/location — change the DWH region (ADR-0058) */
export async function changeDwhLocation(payload: {
  bq_location: string;
}): Promise<DwhStatus> {
  return apiJson<DwhStatus>("/accounts/dwh/location", {
    method: "PATCH",
    body: JSON.stringify(payload),
    unwrapData: false,
  });
}

/** GET /accounts/dwh/grant-instructions */
export type DwhGrantInstructions = {
  cloud_shell_url: string;
  gcloud_commands: string[];
  runtime_sa_email: string;
  dwh_project_id: string;
};

export async function getDwhGrantInstructions(): Promise<DwhGrantInstructions> {
  return apiJson<DwhGrantInstructions>("/accounts/dwh/grant-instructions", {
    unwrapData: false,
  });
}

/** POST /accounts/dwh/provision */
export async function provisionDwh(payload: {
  bq_location: string;
  custom_project_name?: string;
}): Promise<DwhStatus> {
  return apiJson<DwhStatus>("/accounts/dwh/provision", {
    method: "POST",
    body: JSON.stringify(payload),
    unwrapData: false,
  });
}

/** POST /accounts/dwh/connect */
export async function connectDwh(payload: {
  bq_project_id: string;
  bq_location: string;
}): Promise<DwhStatus> {
  return apiJson<DwhStatus>("/accounts/dwh/connect", {
    method: "POST",
    body: JSON.stringify(payload),
    unwrapData: false,
  });
}

/** POST /accounts/dwh/validate */
export type DwhValidateResult = {
  provisioning_status: string;
  validation_errors: string[];
  /** Non-blocking over-grant warnings (ADR-0328); absent on an older backend. */
  validation_warnings?: string[];
  runtime_sa_email: string | null;
};

export async function validateDwh(): Promise<DwhValidateResult> {
  return apiJson<DwhValidateResult>("/accounts/dwh/validate", {
    method: "POST",
    unwrapData: false,
  });
}

/** POST /accounts/dwh/disconnect */
export async function disconnectDwh(): Promise<DwhStatus> {
  return apiJson<DwhStatus>("/accounts/dwh/disconnect", {
    method: "POST",
    unwrapData: false,
  });
}
